import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Radio, GraduationCap, Users, Trophy, LogOut, FileDown, ShieldAlert } from "lucide-react";
import SpeakerButton from "./SpeakerButton";
import StreakBadge from "./StreakBadge";
import { clearActiveCadet, downloadWeeklyReport } from "../lib/api";

const items = [
  { to: "/", label: "Bridge", icon: Radio, testid: "nav-bridge" },
  { to: "/academy", label: "Academy", icon: GraduationCap, testid: "nav-academy" },
  { to: "/mentors", label: "Mentors", icon: Users, testid: "nav-mentors" },
  { to: "/sentinel", label: "Sentinel", icon: ShieldAlert, testid: "nav-sentinel" },
];

const getPageName = (pathname) => {
  if (pathname === "/academy") return "Signal Science Academy";
  if (pathname === "/mentors") return "Mentor-Cadet Bridge";
  if (pathname === "/sentinel") return "Sovereign Sentinel Vault";
  return "CyberKids Academy";
};

export const Navigation = ({ cadet, streakDays = 0 }) => {
  const loc = useLocation();
  const pageName = getPageName(loc.pathname);
  return (
    <nav className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-yellow-300/15" data-testid="top-nav">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-yellow-300 to-yellow-600 flex items-center justify-center eigoo-glow-gold">
            <Radio className="text-black" size={20} />
          </div>
          <div className="leading-tight">
            <div className="text-[9px] md:text-[10px] uppercase tracking-[0.3em] text-yellow-300/80 font-display">CyberKids · Sovereign Fleet</div>
            <div className="font-display text-lg md:text-xl font-black">{pageName}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-full bg-white/5 border border-yellow-300/15">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `px-3 md:px-4 py-2 rounded-full font-display font-bold text-sm flex items-center gap-1.5 transition-all ${
                    isActive
                      ? "bg-gradient-to-br from-yellow-300 to-yellow-600 text-black shadow-[0_6px_20px_rgba(255,193,7,0.35)]"
                      : "text-white/80 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Icon size={16} />
                <span className="hidden sm:inline">{it.label}</span>
              </NavLink>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <StreakBadge days={streakDays} size="sm" testid="nav-streak-badge" />
          {cadet && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-300/10 border border-yellow-300/30" data-testid="nav-cadet-badge">
              <Trophy size={14} className="text-yellow-300" />
              <span className="font-display font-bold text-sm text-yellow-100">{cadet.callsign || "Commander"}</span>
              <span className="text-[10px] uppercase tracking-widest text-yellow-300/70">{cadet.rank}</span>
            </div>
          )}
          <button
            onClick={async () => { try { await downloadWeeklyReport(); } catch(e) { alert("Report failed."); } }}
            className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 hover:bg-yellow-300/15 border border-yellow-300/25 text-yellow-200 text-xs font-display font-bold"
            title="Download weekly Sovereign report"
            data-testid="nav-download-report"
          >
            <FileDown size={14} /> Report
          </button>
          <button
            onClick={() => { clearActiveCadet(); window.dispatchEvent(new Event("cyberkids:switch-cadet")); }}
            className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 hover:bg-red-500/15 border border-white/10 text-white/70 text-xs font-display font-bold"
            title="Switch cadet"
            data-testid="nav-switch-cadet"
          >
            <LogOut size={14} /> Switch
          </button>
          <SpeakerButton text={`You are on the ${pageName}.`} testid="nav-speaker" size="sm" />
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
